import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { PostCardAction } from '@components/community/CharacterPost';

import {
  deleteCharacterPost,
  patchCharacterPost,
  toggleCharacterPostLike,
} from '@api/community';
import { IGroundFeed } from '@api/feed';
import { Platform, QUERY_KEYS, ReportObjectType } from '@constants';
import { useGlobalContext } from '@contexts/global';
import {
  ICharacterPost,
  IChatPaginationClientResponse,
  IEFinderObject,
} from '@model';
import { toQueryString } from '@utils/misc';
import { useOptimisticUpdate } from '@utils/query';
import { useWebView } from '@utils/webview';

import { useModal } from './useModal';

export const useCharacterPost = (feedCode?: string) => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const { platform } = useGlobalContext();
  const router = useRouter();
  const queryClient = useQueryClient();
  const imageModalName = 'image-modal';

  const [
    isImageModalOpen,
    { open: oepnImageModal, close: closeImageModal },
    selectedImage,
  ] = useModal<IEFinderObject<'image'>>(imageModalName);

  const optimistic = useOptimisticUpdate();
  const { mutate: toggleLike } = useMutation(toggleCharacterPostLike);

  const { mutate: removePost } = useMutation((postId: number) =>
    deleteCharacterPost(postId)
  );

  const { mutate: updateCommunityPost } = useMutation(patchCharacterPost);

  const pollVote = useCallback(
    (post: ICharacterPost, characterId: number) => {
      optimistic.update<ICharacterPost>(
        QUERY_KEYS.POST.CHARACTER_LIST(characterId),
        (p) => ({ ...p, poll: post.poll }),
        { id: post.id }
      );
      if (feedCode) {
        queryClient.invalidateQueries(QUERY_KEYS.OUT_LOOK.GROUND_FEED_ALL);
      }
    },
    []
  );

  const likePost = useCallback(
    (post: ICharacterPost, characterId: number, query?: string) => {
      toggleLike(post.id, {
        onSuccess: ({ isLiked }) => {
          optimistic.update<ICharacterPost>(
            QUERY_KEYS.POST.CHARACTER_LIST(characterId as number),
            (post) => ({
              ...post,
              isLiked,
              likeCount: post.likeCount + (isLiked ? 1 : -1),
            }),
            { id: post.id }
          );
          if (feedCode) {
            queryClient.setQueryData<
              IChatPaginationClientResponse<IGroundFeed> | undefined
            >(QUERY_KEYS.OUT_LOOK.GROUND_FEED(feedCode, query), (prevData) => {
              if (!prevData) return prevData;
              const newData = {
                ...prevData,
                pages: prevData.pages.map((page) => ({
                  ...page,
                  items: page.items.map((item) => {
                    if (item?.post && item.post.id === post.id) {
                      return {
                        ...item,
                        post: {
                          ...item.post,
                          isLiked: isLiked,
                          likeCount: post.likeCount + (isLiked ? 1 : -1),
                        },
                      };
                    } else {
                      return item;
                    }
                  }),
                })),
              };
              return newData;
            });
          }
        },
      });
    },
    []
  );

  const handleNavigatePostDetail = (
    characterId: number,
    id: number,
    userId: number
  ) => {
    if (platform === Platform.web) {
      router.push(
        `/play/characters/${characterId}/posts/${id}${toQueryString({
          playerId: userId,
        })}`
      );
    }
    web.openPage(
      `/play/characters/${characterId}/posts/${id}${toQueryString({
        playerId: userId,
      })}`
    );
  };

  const handleDeletePost = (postId: number, characterId: number) => {
    if (confirm(t('정말 게시글을 삭제하시겠습니까?'))) {
      removePost(postId, {
        onSuccess: () => {
          queryClient.invalidateQueries(
            QUERY_KEYS.POST.CHARACTER_LIST(characterId)
          );
          if (feedCode) {
            queryClient.invalidateQueries(QUERY_KEYS.OUT_LOOK.GROUND_FEED_ALL);
          }
        },
      });
    }
  };

  const handleFixPost = (
    postId: number,
    type: 'fix' | 'unfix',
    characterId: number
  ) => {
    const payload = {
      isFixed: type === 'fix' ? true : false,
    };
    updateCommunityPost(
      { postId, ...payload },
      {
        onSuccess: (post) => {
          queryClient.invalidateQueries(
            QUERY_KEYS.POST.CHARACTER_LIST(characterId)
          );

          web.mutate('update_character_community', post);
        },
        onError: () => {
          alert('게시글 고정을 할 수 없습니다.');
        },
      }
    );
  };

  const handleClickTag = (id: number, name: string) => {
    if (platform === Platform.web) {
      router.push(`/play/characters/tags/${id}?title=${name}`);
    } else {
      web.openPage(`/play/characters/tags/${id}?title=${name}`);
    }
  };

  const handleClickImg = (img: IEFinderObject<'image'>) => {
    if (img) {
      oepnImageModal(img);
    }
  };

  const handleActionSheet = (
    value: PostCardAction,
    id: number,
    makerId: number,
    characterId: number
  ) => {
    switch (value) {
      case 'modify':
        queryClient.invalidateQueries(QUERY_KEYS.OUT_LOOK.GROUND_FEED_ALL);
        if (platform === Platform.web) {
          router.push(
            `/play/characters/posts/create${toQueryString({
              playerId: makerId,
              postId: id,
            })}`
          );
        }
        web.openPage(
          `/play/characters/posts/create${toQueryString({
            playerId: makerId,
            postId: id,
          })}`
        );
        break;
      case 'delete':
        handleDeletePost(id, characterId);
        break;
      case 'report':
        router.push(
          `/report${toQueryString({
            objectType: ReportObjectType.character_community,
            objectId: id,
          })}`
        );
        break;
      case 'fix':
        handleFixPost(id, 'fix', characterId);
        break;
      case 'unfix':
        handleFixPost(id, 'unfix', characterId);
        break;
      default:
        return;
    }
  };
  return {
    handleActionSheet,
    likePost,
    pollVote,
    handleNavigatePostDetail,
    handleClickTag,
    handleClickImg,
    closeImageModal,
    isImageModalOpen,
    selectedImage,
  };
};
